import * as React from 'react';
import { jwtDecode } from 'jwt-decode';
import { SystemRole } from '../../helper-types/general/enums/system-role';

interface User {
    id: number;
    name: string;
    email: string;
    image: string;
    isAdmin: boolean;
    isPhysician: boolean;
}

interface AuthContextType {
    isAuthenticated: boolean;
    userRoles: string[];
    user: User | null;
    clinicName: string | null;
    physicianId: string | null;
    averageConsultationTimeInMinutes: number;
    isLoading: boolean;
    login: (token: string) => void;
    logout: () => void;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = React.useState(false);
    const [userRoles, setUserRoles] = React.useState<string[]>([]);
    const [user, setUser] = React.useState<User | null>(null);
    const [clinicName, setClinicName] = React.useState<string | null>(null);
    const [physicianId, setPhysicianId] = React.useState<string | null>(null);
    const [averageConsultationTimeInMinutes, setAverageConsultationTimeInMinutes] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState(true);

    const login = (token: string) => {
        localStorage.setItem('bearerToken', token);

        try {
            const decoded: { [key: string]: any } = jwtDecode(token);

            const roleClaim = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
            const roles = roleClaim
                ? Array.isArray(roleClaim)
                    ? roleClaim
                    : [roleClaim]
                : [];

            const userData: User = {
                id: decoded['sid'] || 0,
                name: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || '',
                email: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || '',
                image: '',
                isAdmin: decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']?.includes(SystemRole.Administrator) || false,
                isPhysician: decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']?.includes(SystemRole.Physician) || false,
            };

            const clinicName = decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/locality'] || null;
            const physicianId = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/primarygroupsid'] || null;
            const averageConsultationTimeInMinutes = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata'] || 0;

            setUserRoles(roles);
            setUser(userData);
            setClinicName(clinicName);
            setPhysicianId(physicianId);
            setAverageConsultationTimeInMinutes(averageConsultationTimeInMinutes);
            setIsAuthenticated(true);
        } catch (error) {
            setUserRoles([]);
            setUser(null);
            setIsAuthenticated(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('bearerToken');
        setIsAuthenticated(false);
        setUserRoles([]);
        setUser(null);
        setClinicName(null);
        setPhysicianId(null);
        setAverageConsultationTimeInMinutes(0);
    };

    React.useEffect(() => {
        const token = localStorage.getItem('bearerToken');
        if (token) {
            try {
                const decoded: { exp?: number } = jwtDecode(token);

                if (decoded.exp && decoded.exp * 1000 < Date.now()) {
                    logout();
                } else {
                    login(token);
                }
            } catch (error) {
                logout();
            }
        }
        setIsLoading(false);
    }, []);

    return (
        <AuthContext.Provider value={{ isAuthenticated, userRoles, user, clinicName, physicianId, averageConsultationTimeInMinutes, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = React.useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};